import { InputHTMLAttributes, ReactNode, useId } from 'react';

export type OtsSwitchSize = 'compact' | 'default';

export type OtsSwitchProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> & {
  label?: ReactNode;
  description?: ReactNode;
  size?: OtsSwitchSize;
  onCheckedChange?: (checked: boolean) => void;
};

/** Accessible OTS toggle with a compact variant for dense embedded surfaces. */
export const OtsSwitch = ({
  id: idProp,
  label,
  description,
  size = 'default',
  className,
  disabled,
  onCheckedChange,
  onChange,
  ...props
}: OtsSwitchProps) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const compact = size === 'compact';

  return (
    <label
      htmlFor={id}
      className={[
        'inline-flex min-w-0 items-start select-none',
        compact ? 'gap-2 text-xs' : 'gap-3 text-sm',
        disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        className={[
          'relative mt-0.5 inline-flex shrink-0',
          compact ? 'h-4 w-7' : 'h-5 w-9',
        ].join(' ')}
      >
        <input
          {...props}
          id={id}
          type="checkbox"
          role="switch"
          disabled={disabled}
          onChange={(event) => {
            onChange?.(event);
            onCheckedChange?.(event.currentTarget.checked);
          }}
          className="peer sr-only"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full border border-[var(--ots-outline)] bg-[var(--ots-surface-variant)] transition-colors peer-checked:border-[var(--ots-primary)] peer-checked:bg-[var(--ots-primary)] peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--ots-primary)]"
        />
        <span
          aria-hidden="true"
          className={[
            'pointer-events-none absolute rounded-full bg-[var(--ots-text)] shadow-sm transition-all peer-checked:bg-[var(--ots-on-primary)]',
            compact
              ? 'left-0.5 top-0.5 size-3 peer-checked:translate-x-3'
              : 'left-0.5 top-0.5 size-4 peer-checked:translate-x-4',
          ].join(' ')}
        />
      </span>
      {(label || description) && (
        <span className="min-w-0">
          {label && (
            <span className="block font-semibold leading-5 text-[var(--ots-secondary)]">
              {label}
            </span>
          )}
          {description && (
            <span
              className={[
                'block leading-5 text-[var(--ots-text-muted)]',
                label ? 'mt-0.5' : '',
              ].join(' ')}
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  );
};
